import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Tray,
  MagnifyingGlass as Search,
  CircleNotch,
  Buildings,
  User,
  Scales,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight,
  Warning,
  FireSimple,
  Envelope,
  Phone,
  Copy,
} from '@phosphor-icons/react';
import {
  fetchIntakes,
  updateIntake,
  convertIntake,
  type IntakeRequest,
  type IntakeStatus,
  type IntakeStatusCounts,
  type IntakeArrivalChannel,
  type IntakeInstructingPartyType,
  type EnquiryHold,
  fetchIntake,
  holdDateForEnquiry,
  releaseHold,
} from '@/lib/intakeApi';
import { LogEnquiryModal } from '@/components/shared/LogEnquiryModal';
import { Plus } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatCard } from '@/components/shared/StatCard';
import { useAuthStore } from '@/features/auth/store';
import { getApiErrorMessage } from '@/lib/api';
import { InviteSolicitorModal } from '@/components/shared/InviteSolicitorModal';
import { UserPlus } from '@phosphor-icons/react';

// Ordered by where an enquiry sits in the working sequence, not alphabetically and not by
// the database enum — a clerk reads this left to right as the life of a piece of work.
const STATUS_TABS: Array<{ key: IntakeStatus | 'all'; label: string }> = [
  { key: 'new', label: 'New' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'assessing', label: 'Assessing' },
  { key: 'quoted', label: 'Quoted' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'rejected', label: 'Declined' },
  { key: 'lapsed', label: 'Lapsed' },
  { key: 'converted', label: 'Converted' },
  { key: 'all', label: 'All' },
];

export const ARRIVAL_LABELS: Record<IntakeArrivalChannel, string> = {
  phone: 'Telephone',
  email: 'Email',
  web_form: 'Website form',
  portal: 'Court portal',
  direct: 'Direct to counsel',
  referral: 'Referral',
  internal: 'Raised internally',
};

export const PARTY_TYPE_LABELS: Record<IntakeInstructingPartyType, string> = {
  professional_client: 'Solicitor / professional client',
  lay_client: 'Lay client (Public Access)',
  portal: 'Court / legal aid portal',
  internal: 'Chambers (internal)',
};

export default function IntakeInboxPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const basePath = getBasePath(user?.memberType);
  const isBarrister =
    user?.memberType === 'silk' || user?.memberType === 'junior' || user?.memberType === 'pupil';

  const [tab, setTab] = useState<IntakeStatus | 'all'>('new');
  const [requests, setRequests] = useState<IntakeRequest[]>([]);
  const [counts, setCounts] = useState<IntakeStatusCounts>({
    new: 0, reviewing: 0, assessing: 0, quoted: 0,
    accepted: 0, rejected: 0, lapsed: 0, converted: 0, total: 0,
  });
  const [loading, setLoading] = useState(true);
  /** Set when the server refuses this role outright (403) — distinct from an empty queue. */
  const [refused, setRefused] = useState(false);
  const [selected, setSelected] = useState<IntakeRequest | null>(null);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await fetchIntakes(tab === 'all' ? undefined : tab);
      setRequests(res.requests);
      setCounts(res.statusCounts);
      setRefused(false);
    } catch (err) {
      // INTAKE_ROLES (intake.routes.ts) does not serve pupils or door tenants, so for them this
      // answers 403 — and the page then rendered "No intakes here … when a solicitor submits
      // one it will appear here", which is a refusal disguised as an empty queue that will fill
      // up later. Record the refusal so the empty state can tell the truth. No access changes.
      if ((err as { response?: { status?: number } })?.response?.status === 403) {
        setRefused(true);
      } else {
        toast.error(getApiErrorMessage(err, 'Could not load intake inbox'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Actionable tabs are a triage queue, not a feed: urgent instructions pinned to
  // the top, then oldest-waiting first so nothing rots unactioned at the bottom.
  // Closed tabs (rejected/converted) stay newest-decision-first.
  const isTriageView = tab === 'new' || tab === 'reviewing' || tab === 'all';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? requests.filter((r) =>
          [r.solicitorFirmName, r.solicitorName, r.layClientName, r.summary, r.opposingPartyName]
            .filter(Boolean)
            .some((f) => f!.toLowerCase().includes(q)),
        )
      : requests;

    const rows = [...base];
    if (isTriageView) {
      rows.sort((a, b) => {
        const pin = urgencyPin(a.urgency) - urgencyPin(b.urgency); // urgent (0) floats up
        if (pin !== 0) return pin;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(); // oldest first
      });
    } else {
      rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return rows;
  }, [requests, search, isTriageView]);

  // The public intake link is ALWAYS this chambers' own slug — taken from the logged-in session,
  // never from requests[0] (which is empty on a fresh inbox and previously fell back to a hardcoded
  // 'smith-co-chambers', producing a link that pointed a solicitor at the WRONG chambers so their
  // submission — and its notification — went to a different tenant entirely).
  const chambersSlug = useAuthStore((s) => s.chambers?.slug ?? null);
  const publicFormUrl = useMemo(() => {
    if (typeof window === 'undefined' || !chambersSlug) return '';
    return `${window.location.origin}/intake/${chambersSlug}`;
  }, [chambersSlug]);

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Intake Inbox"
        subtitle="Incoming instructions from solicitors — urgent pinned, longest-waiting first. Review, accept, or reject before creating a matter."
        actions={
          // Three actions at full size measure 518px, which on a 390px phone used to push the
          // primary one off the screen entirely. PageHeader now stacks a header's actions below
          // sm, so each of these gets a full-width row and keeps its whole label; they return to
          // a content-sized row at 640px.
          // justify-end below: PageHeader right-aligns its action CHILDREN, but this page hands
          // it one w-full wrapper, so the alignment had nothing to bite on and the three buttons
          // sat at the left edge of a group that stretches to the page edge — ~300px of gap to
          // their right. Only from sm, where the group is a row; below sm it is a full-width
          // stack and justification is a no-op. The spacing itself is PageHeader's, not this
          // page's — gap-2 here only has to match it below sm.
          <div className="flex w-full flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
            <Button
              variant="outline"
              size="md"
              className="grow justify-center sm:grow-0"
              leftIcon={<Copy className="h-4 w-4" />}
              onClick={() => {
                if (publicFormUrl) {
                  void navigator.clipboard.writeText(publicFormUrl);
                  toast.success('Public intake URL copied to clipboard');
                }
              }}
            >
              Copy public link
            </Button>
            <Button
              variant="outline"
              size="md"
              className="grow justify-center sm:grow-0"
              leftIcon={<UserPlus className="h-4 w-4" />}
              onClick={() => setInviteOpen(true)}
            >
              Invite solicitor
            </Button>
            <Button
              variant="primary"
              size="md"
              className="grow justify-center sm:grow-0"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setLogOpen(true)}
            >
              Log an enquiry
            </Button>
          </div>
        }
      />

      <InviteSolicitorModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <LogEnquiryModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onLogged={() => { void reload(); }}
      />

      {/* Stat cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard label="Awaiting review" value={counts.new} icon={<Tray className="h-5 w-5" />} />
        <StatCard label="In review" value={counts.reviewing} icon={<Clock className="h-5 w-5" />} />
        <StatCard label="Accepted" value={counts.accepted} icon={<CheckCircle className="h-5 w-5" />} />
        <StatCard label="Rejected" value={counts.rejected} icon={<XCircle className="h-5 w-5" />} />
      </div>

      {/* Tabs + search */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex flex-wrap items-center gap-1">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                  tab === t.key
                    ? 'bg-brand-600 text-white'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {t.label}
                {t.key !== 'all' && (
                  <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${
                    tab === t.key ? 'bg-white/20' : 'bg-muted-foreground/10'
                  }`}>
                    {counts[t.key as IntakeStatus] ?? 0}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="relative w-full">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search firm, client, party…"
              className="h-9 w-full rounded-md border border-border bg-card pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/40"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircleNotch className="h-6 w-6 animate-spin text-brand-600 dark:text-brand-400" />
          </div>
        ) : refused ? (
          <EmptyState
            icon={<Tray className="h-6 w-6" />}
            title="The intake inbox isn't available to your role"
            description="Triaging new enquiries is restricted to tenants and chambers staff. This queue is not empty — it is not yours to see."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Tray className="h-6 w-6" />}
            title="No intakes here"
            description={
              tab === 'new'
                ? 'When a solicitor submits the public intake form, it will appear here for review.'
                : 'No intakes match this filter.'
            }
          />
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className="flex w-full items-start gap-4 px-5 py-4 text-left transition hover:bg-muted/40"
              >
                <UrgencyDot urgency={r.urgency} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {r.layClientName}
                    </div>
                    <StatusBadge status={r.status} />
                    <ConflictChip status={r.conflictStatus} />
                    {r.opposingPartyName && (
                      <span className="text-xs text-muted-foreground">
                        v {r.opposingPartyName}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {/* An enquiry can have no instructing solicitor (Direct Access, a court
                        portal, an internal origin). Show how it arrived instead of an empty
                        row, so the line still tells the clerk something. */}
                    {r.solicitorFirmName ? (
                      <span className="inline-flex items-center gap-1">
                        <Buildings className="h-3.5 w-3.5" /> {r.solicitorFirmName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        <Buildings className="h-3.5 w-3.5" /> {ARRIVAL_LABELS[r.arrivalChannel]}
                      </span>
                    )}
                    {r.solicitorName && (
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3.5 w-3.5" /> {r.solicitorName}
                      </span>
                    )}
                    {r.practiceArea && (
                      <span className="inline-flex items-center gap-1">
                        <Scales className="h-3.5 w-3.5" /> {r.practiceArea}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" /> {formatRelative(r.createdAt)}
                    </span>
                    <AgeBadge request={r} />
                  </div>
                  <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{r.summary}</div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {selected && (
        <DetailDrawer
          request={selected}
          leadBarristerId={isBarrister ? user?.barristerId ?? undefined : undefined}
          onClose={() => setSelected(null)}
          onChanged={(updated) => {
            setSelected(updated);
            void reload();
          }}
          onConverted={(matter) => {
            setSelected(null);
            navigate(`${basePath}/matters/${matter.id}`);
          }}
        />
      )}
    </div>
  );
}

// ─── Detail drawer ─────────────────────────────────────────

function DetailDrawer({
  request,
  leadBarristerId,
  onClose,
  onChanged,
  onConverted,
}: {
  request: IntakeRequest;
  /** When a barrister converts their own intake, they become lead immediately. */
  leadBarristerId?: string;
  onClose: () => void;
  onChanged: (r: IntakeRequest) => void;
  onConverted: (matter: { id: string; caseNumber: string; caseName: string }) => void;
}) {
  const [notes, setNotes] = useState(request.reviewNotes ?? '');
  const [rejectReason, setRejectReason] = useState(request.rejectionReason ?? '');
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState<'accept' | 'reject' | 'reviewing' | 'save' | 'convert' | null>(null);
  const isCritical = request.conflictStatus === 'critical';

  const act = async (patch: Parameters<typeof updateIntake>[1], tag: typeof busy) => {
    setBusy(tag);
    try {
      const updated = await updateIntake(request.id, patch);
      onChanged(updated);
      toast.success('Updated');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not update'));
    } finally {
      setBusy(null);
    }
  };

  // One atomic action: the accepted instruction becomes a live, operational
  // matter (solicitor contact, opponent, court, fee, hearing+diary, chronology
  // all carried server-side). No re-typing, no orphan-matter window.
  const doConvert = async () => {
    setBusy('convert');
    try {
      const matter = await convertIntake(request.id, {
        ...(leadBarristerId ? { leadBarristerId } : {}),
        ...(isCritical && overrideReason.trim() ? { conflictOverrideReason: overrideReason.trim() } : {}),
      });
      toast.success(`Matter created — ${matter.caseNumber}`);
      onConverted(matter);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not convert to matter'));
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="flex w-full max-w-xl flex-col overflow-y-auto bg-card shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-base font-semibold text-foreground">{request.layClientName}</h2>
              <StatusBadge status={request.status} />
            </div>
            <div className="text-xs text-muted-foreground">
              Received {formatRelative(request.createdAt)} · Ref {request.id.slice(0, 8).toUpperCase()}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 px-6 py-5">
          <ConflictBanner request={request} />

          <Block title={request.instructingPartyType === 'professional_client' ? 'Solicitor' : 'Instructing party'}>
            <KV k="Arrived by" v={ARRIVAL_LABELS[request.arrivalChannel]} />
            <KV k="Instructed by" v={PARTY_TYPE_LABELS[request.instructingPartyType]} />
            {/* Every solicitor field is optional now, so each is rendered only when present.
                A public-access enquiry legitimately has none of them. */}
            {request.solicitorFirmName && <KV k="Firm" v={request.solicitorFirmName} />}
            {request.solicitorName && <KV k="Name" v={request.solicitorName} />}
            {request.solicitorEmail && (
              <KV
                k="Email"
                v={request.solicitorEmail}
                icon={<Envelope className="h-3.5 w-3.5" />}
                link={`mailto:${request.solicitorEmail}`}
              />
            )}
            {request.solicitorPhone && (
              <KV k="Phone" v={request.solicitorPhone} icon={<Phone className="h-3.5 w-3.5" />} />
            )}
            {request.solicitorSraNumber && <KV k="SRA number" v={request.solicitorSraNumber} />}
            {request.instructingPartyType !== 'professional_client' && (
              <p className="pt-1 text-xs text-muted-foreground">
                No instructing solicitor — this enquiry came directly to chambers.
              </p>
            )}
          </Block>

          <Block title="Client">
            <KV k="Lay client" v={request.layClientName} />
            <KV k="Type" v={request.layClientType === 'company' ? 'Company' : 'Individual'} />
          </Block>

          {(request.opposingPartyName || request.opposingSolicitor) && (
            <Block title="Opposing side">
              {request.opposingPartyName && <KV k="Party" v={request.opposingPartyName} />}
              {request.opposingSolicitor && <KV k="Solicitor" v={request.opposingSolicitor} />}
            </Block>
          )}

          <Block title="Matter">
            {request.practiceArea && <KV k="Practice area" v={request.practiceArea} />}
            {request.courtName && <KV k="Court" v={request.courtName} />}
            {request.hearingDate && <KV k="Hearing date" v={new Date(request.hearingDate).toLocaleDateString('en-GB')} />}
            <KV k="Urgency" v={<UrgencyBadge urgency={request.urgency} />} />
            {request.feeExpectation && <KV k="Fee expectation" v={request.feeExpectation} />}
          </Block>

          <Block title="Summary">
            <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-sm text-foreground">
              {request.summary}
            </div>
          </Block>

          {request.status !== 'converted' && (
            <HoldPanel request={request} />
          )}

          {/* Review area */}
          {request.status !== 'converted' && (
            <Block title="Review">
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Internal notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Notes visible only to chambers staff…"
                className="w-full resize-none rounded-md border border-border bg-card p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              />
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={busy !== null}
                onClick={() => act({ reviewNotes: notes, status: 'reviewing' }, 'save')}
              >
                {busy === 'save' ? 'Saving…' : 'Save notes'}
              </Button>
            </Block>
          )}

          {request.status === 'rejected' && request.rejectionReason && (
            <Block title="Rejection reason">
              <div className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-900 dark:text-red-200">
                {request.rejectionReason}
              </div>
            </Block>
          )}

          {request.status === 'accepted' && (
            <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-3 text-xs text-emerald-900 dark:text-emerald-200">
              <strong>Accepted.</strong> Create the matter when ready — it'll link back to this intake.
            </div>
          )}
        </div>

        {/* Actions footer */}
        <div className="sticky bottom-0 border-t border-border bg-card px-6 py-4">
          {request.status === 'new' || request.status === 'reviewing' ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="md"
                  className="flex-1"
                  disabled={busy !== null || (isCritical && overrideReason.trim().length === 0)}
                  leftIcon={<CheckCircle className="h-4 w-4" />}
                  onClick={() => act({
                    status: 'accepted',
                    reviewNotes: notes || undefined,
                    ...(isCritical && overrideReason.trim() ? { conflictOverrideReason: overrideReason.trim() } : {}),
                  }, 'accept')}
                >
                  {busy === 'accept' ? 'Accepting…' : isCritical ? 'Override & accept' : 'Accept'}
                </Button>
                <Button
                  variant="destructive"
                  size="md"
                  className="flex-1"
                  disabled={busy !== null || rejectReason.trim().length === 0}
                  leftIcon={<XCircle className="h-4 w-4" />}
                  onClick={() => act({ status: 'rejected', rejectionReason: rejectReason }, 'reject')}
                >
                  {busy === 'reject' ? 'Rejecting…' : 'Reject'}
                </Button>
              </div>
              {isCritical && (
                <input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Critical conflict — documented override reason (silk/KC or admin only)"
                  className="h-9 w-full rounded-md border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-3 text-xs text-red-900 dark:text-red-200 placeholder:text-red-400 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              )}
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Rejection reason (required to reject)"
                className="h-9 w-full rounded-md border border-border bg-card px-3 text-xs focus:outline-none focus:ring-2 focus:ring-brand-600/40"
              />
            </div>
          ) : request.status === 'accepted' ? (
            <div className="space-y-3">
              {isCritical && (
                <input
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder="Critical conflict — documented override reason (silk/KC or admin only)"
                  className="h-9 w-full rounded-md border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-3 text-xs text-red-900 dark:text-red-200 placeholder:text-red-400 focus:outline-none focus:ring-2 focus:ring-red-500/40"
                />
              )}
              <Button
                variant="primary"
                size="md"
                className="w-full"
                disabled={busy !== null || (isCritical && overrideReason.trim().length === 0)}
                leftIcon={busy === 'convert' ? <CircleNotch className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                onClick={() => void doConvert()}
              >
                {busy === 'convert' ? 'Creating matter…' : 'Convert to matter'}
              </Button>
            </div>
          ) : (
            <div className="text-center text-xs text-muted-foreground">
              This intake is {request.status}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Small UI helpers ─────────────────────────────────────

function ConflictChip({ status }: { status: IntakeRequest['conflictStatus'] }) {
  if (status === 'critical') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-500/15 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300">
        <Warning className="h-3 w-3" weight="fill" /> CONFLICT
      </span>
    );
  }
  if (status === 'potential') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
        <Warning className="h-3 w-3" /> Check
      </span>
    );
  }
  return null;
}

function ConflictBanner({ request }: { request: IntakeRequest }) {
  const status = request.conflictStatus;
  if (!status || !request.conflictScannedAt) {
    return (
      <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        Conflict check has not been run for this instruction.
      </div>
    );
  }
  if (status === 'clear') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-800 dark:text-emerald-300">
        <CheckCircle className="h-4 w-4" weight="fill" /> Conflict check clear — no match against existing matters.
      </div>
    );
  }
  const critical = status === 'critical';
  const details = request.conflictDetails ?? [];
  return (
    <div className={`rounded-md border p-3 text-xs ${critical ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 text-red-900 dark:text-red-200' : 'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200'}`}>
      <div className="mb-1.5 flex items-center gap-2 font-semibold">
        <Warning className="h-4 w-4" weight="fill" />
        {critical
          ? 'CRITICAL conflict — acceptance blocked without a silk (KC) or admin override'
          : 'Possible conflict — review carefully before accepting'}
      </div>
      <ul className="ml-1 list-disc space-y-1 pl-4">
        {details.slice(0, 5).map((d, i) => (
          <li key={i}>
            <span className="font-medium">{d.existingMatterNumber}</span> {d.existingMatterName}: {d.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusBadge({ status }: { status: IntakeStatus }) {
  const map: Record<IntakeStatus, { label: string; variant: 'info' | 'warning' | 'success' | 'danger' | 'default' }> = {
    new: { label: 'New', variant: 'info' },
    reviewing: { label: 'Reviewing', variant: 'warning' },
    assessing: { label: 'Assessing', variant: 'warning' },
    quoted: { label: 'Quoted', variant: 'info' },
    accepted: { label: 'Accepted', variant: 'success' },
    rejected: { label: 'Declined', variant: 'danger' },
    // Went quiet rather than was refused — deliberately neutral, not a failure state.
    lapsed: { label: 'Lapsed', variant: 'default' },
    converted: { label: 'Converted', variant: 'default' },
  };
  const m = map[status];
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

function UrgencyBadge({ urgency }: { urgency: IntakeRequest['urgency'] }) {
  if (urgency === 'urgent') return <Badge variant="danger">Urgent</Badge>;
  if (urgency === 'expedited') return <Badge variant="warning">Expedited</Badge>;
  return <Badge variant="default">Routine</Badge>;
}

function UrgencyDot({ urgency }: { urgency: IntakeRequest['urgency'] }) {
  if (urgency === 'urgent') return <FireSimple className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" weight="fill" />;
  if (urgency === 'expedited') return <Warning className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />;
  return <Tray className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />;
}

/**
 * The clerk's PENCIL.
 *
 * "Are you free on the 19th?" is the commonest thing a clerks' room is asked, and the answer has to
 * hold the date without promising it. A hold occupies the diary and shows in free/busy, so counsel
 * is not double-booked — but it commits chambers to nothing, and most of them are released.
 *
 * Deliberately NOT synced to the barrister's Google/Outlook calendar: a pencil that reads as a
 * booking to everyone sharing that calendar is worse than no entry at all. On conversion these are
 * confirmed automatically and re-pointed at the matter.
 */
function HoldPanel({ request }: { request: IntakeRequest }) {
  // The panel owns its holds. The drawer opens with the row from the LIST, which deliberately
  // carries no holds — fetching them per row would cost a query each, and a triage queue has no
  // use for them. Without this the panel said "No dates held" for an enquiry that HAS holds, and a
  // clerk would pencil a second date over one already blocked. Found in the browser, not in tests:
  // creating a hold worked, so the panel looked correct until the drawer was closed and reopened.
  const [holds, setHolds] = useState<EnquiryHold[]>(request.calendarEvents ?? []);
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState('');
  const [from, setFrom] = useState('10:00');
  const [to, setTo] = useState('16:00');
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = async () => {
    try { setHolds((await fetchIntake(request.id)).calendarEvents ?? []); }
    catch { /* leave what is on screen rather than blanking it */ }
  };

  // Load the holds whenever a different enquiry is opened in the drawer.
  useEffect(() => { void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id]);

  const place = async () => {
    if (!date) { toast.error('Pick a date to hold.'); return; }
    setBusy('add');
    try {
      await holdDateForEnquiry({
        intakeId: request.id,
        // Named so it is unmistakable in a diary full of real commitments.
        title: `HOLD — ${request.layClientName}`,
        startAt: new Date(`${date}T${from}:00`).toISOString(),
        endAt: new Date(`${date}T${to}:00`).toISOString(),
      });
      toast.success('Date held. It shows in the diary as provisional, and is not sent to your external calendar.');
      setAdding(false); setDate('');
      await refresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not hold that date'));
    } finally { setBusy(null); }
  };

  const release = async (id: string) => {
    setBusy(id);
    try {
      await releaseHold(id);
      toast.success('Hold released — the diary is free again.');
      await refresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not release the hold'));
    } finally { setBusy(null); }
  };

  return (
    <Block title="Provisional diary holds">
      {holds.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">
          No dates held. Hold a date to keep it free while this enquiry is decided.
        </p>
      )}

      {holds.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {holds.map((h) => (
            <li
              key={h.id}
              className="flex items-center justify-between gap-3 rounded-md border border-dashed border-amber-300 bg-amber-50/50 px-3 py-2 text-xs dark:border-amber-500/40 dark:bg-amber-500/10"
            >
              <span className="min-w-0">
                <span className="font-medium text-foreground">
                  {new Date(h.startAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
                <span className="ml-2 text-muted-foreground">
                  {new Date(h.startAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {' – '}
                  {new Date(h.endAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
              <button
                onClick={() => void release(h.id)}
                disabled={busy === h.id}
                className="shrink-0 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-60"
              >
                {busy === h.id ? 'Releasing…' : 'Release'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="grid grid-cols-3 gap-2">
            <label className="col-span-3 sm:col-span-1">
              <span className="mb-1 block text-xs text-muted-foreground">Date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-card px-2 text-sm" />
            </label>
            <label>
              <span className="mb-1 block text-xs text-muted-foreground">From</span>
              <input type="time" value={from} onChange={(e) => setFrom(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-card px-2 text-sm" />
            </label>
            <label>
              <span className="mb-1 block text-xs text-muted-foreground">To</span>
              <input type="time" value={to} onChange={(e) => setTo(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-card px-2 text-sm" />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setAdding(false); setDate(''); }}
              className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted">
              Cancel
            </button>
            <button onClick={() => void place()} disabled={busy === 'add'}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy === 'add' ? 'Holding…' : 'Hold date'}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted">
          + Hold a date
        </button>
      )}

      <p className="mt-2 text-[11px] text-muted-foreground">
        A hold blocks the diary but is not a commitment, and is not sent to your external calendar.
        Converting this enquiry to a matter confirms any holds automatically.
      </p>
    </Block>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
        {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({
  k, v, icon, link,
}: {
  k: string;
  v: React.ReactNode;
  icon?: React.ReactNode;
  link?: string;
}) {
  const content = (
    <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
      {icon}
      {v}
    </span>
  );
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-xs text-muted-foreground">{k}</dt>
      <dd className="text-right">
        {link ? (
          <a href={link} className="text-brand-600 dark:text-brand-400 hover:underline">{content}</a>
        ) : (
          content
        )}
      </dd>
    </div>
  );
}

// ─── Utilities ─────────────────────────────────────────────

/** Triage pin rank — urgent floats to the very top of the queue. */
function urgencyPin(u: IntakeRequest['urgency']): number {
  return u === 'urgent' ? 0 : 1;
}

/**
 * Response-time SLA (in hours) before an *unactioned* instruction is at risk of
 * being lost — an instruction left waiting is a solicitor who may go elsewhere.
 * Thresholds tighten with urgency. [warnAfter, breachAfter].
 */
const SLA_HOURS: Record<IntakeRequest['urgency'], [number, number]> = {
  urgent: [4, 12],
  expedited: [12, 24],
  routine: [48, 96],
};

/**
 * Age badge for an instruction still waiting on a decision. Green while inside
 * SLA, amber approaching, red once the response window is breached. Only shown
 * for actionable statuses — a decided instruction is no longer "waiting".
 */
function AgeBadge({ request }: { request: IntakeRequest }) {
  if (request.status !== 'new' && request.status !== 'reviewing') return null;
  const ageMs = Date.now() - new Date(request.createdAt).getTime();
  const ageHours = ageMs / 3_600_000;
  const [warn, breach] = SLA_HOURS[request.urgency];
  const tone =
    ageHours >= breach
      ? 'border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300'
      : ageHours >= warn
        ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300'
        : 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  const label =
    ageHours >= breach ? `Overdue · waiting ${formatDuration(ageMs)}` : `Waiting ${formatDuration(ageMs)}`;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>
      <Clock className="h-3 w-3" /> {label}
    </span>
  );
}

/** Compact "waiting" duration — 45m / 6h / 3d. */
function formatDuration(ms: number): string {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-GB');
}

function getBasePath(memberType: string | undefined): string {
  if (memberType === 'senior_clerk' || memberType === 'junior_clerk') return '/clerk';
  if (memberType === 'admin') return '/admin';
  // Barristers (silk/junior/pupil) — a solo barrister manages their own intake,
  // so convert-to-matter must route to /barrister, not /admin (rule #6 basePath).
  if (memberType === 'silk' || memberType === 'junior' || memberType === 'pupil') return '/barrister';
  return '/admin';
}
